import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, Check, Loader2, Briefcase, Heart, Crown, Flower2, Droplets, Wind, TreePine, Flame } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackCustomScentSubmit } from '../utils/analytics';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

const VIBE_OPTIONS = [
  { id: 'Fresh / Clean', label: 'Fresh / Clean', desc: 'Malinis, presko, pang-araw-araw', icon: Wind, color: '#4FC3F7' },
  { id: 'Sweet / Gourmand', label: 'Sweet / Gourmand', desc: 'Matamis, addictive, dessert-like', icon: Droplets, color: '#F48FB1' },
  { id: 'Woody / Masculine', label: 'Woody / Masculine', desc: 'Bold, malalim, commanding', icon: TreePine, color: '#A1887F' },
  { id: 'Floral / Feminine', label: 'Floral / Feminine', desc: 'Elegant, soft, romantic', icon: Flower2, color: '#CE93D8' },
];

const PRESET_ICONS = {
  briefcase: Briefcase, heart: Heart, crown: Crown, flower: Flower2,
};

const FAMILY_COLORS = {
  citrus: '#FFD54F', fruity: '#FF8A65', spicy: '#FF7043', fresh: '#4FC3F7',
  floral: '#CE93D8', sweet: '#F48FB1', woody: '#A1887F',
};

export default function CustomScentPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0=vibe, 1=mainNote, 2=supporting, 3=size, 4=name, 5=review
  const [vibe, setVibe] = useState('');
  const [mainNotes, setMainNotes] = useState([]);
  const [supportingNotes, setSupportingNotes] = useState({ top: '', heart: '', base: '' });
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [scentName, setScentName] = useState('');
  const [aiDescription, setAiDescription] = useState('');
  const [descLoading, setDescLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notesToMaker, setNotesToMaker] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(null);

  useEffect(() => {
    axios.get(`${API}/custom-scent/config`).then(res => {
      setConfig(res.data);
      if (res.data.sizes?.length) setSelectedSize(res.data.sizes[0]);
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error('Failed to load config'); });
  }, []);

  const filteredMainNotes = config?.notes?.filter(n => {
    if (!vibe) return true;
    const vibeMap = {
      'Fresh / Clean': ['citrus', 'fresh'],
      'Sweet / Gourmand': ['sweet'],
      'Woody / Masculine': ['woody', 'spicy'],
      'Floral / Feminine': ['floral'],
    };
    const families = vibeMap[vibe] || [];
    return families.includes(n.family) || n.category === 'heart';
  }) || [];

  const toggleMainNote = (noteId) => {
    setMainNotes(prev => {
      if (prev.includes(noteId)) return prev.filter(n => n !== noteId);
      if (prev.length >= 2) { toast.error('Max 2 main notes lang'); return prev; }
      return [...prev, noteId];
    });
  };

  const fetchSuggestions = useCallback(async () => {
    if (!mainNotes.length) return;
    setSuggestLoading(true);
    try {
      const res = await axios.post(`${API}/custom-scent/suggest-supporting`, {
        vibe, main_notes: mainNotes,
      });
      const { suggestions, ai_recommendation } = res.data;
      setAiSuggestion(ai_recommendation);
      // Auto-fill supporting notes from AI or DB suggestions
      if (ai_recommendation) {
        const findNoteId = (name) => {
          const n = config?.notes?.find(note => note.name.toLowerCase() === name?.toLowerCase());
          return n?.id || '';
        };
        setSupportingNotes({
          top: findNoteId(ai_recommendation.top) || suggestions.top?.[0]?.id || '',
          heart: findNoteId(ai_recommendation.heart) || suggestions.heart?.[0]?.id || '',
          base: findNoteId(ai_recommendation.base) || suggestions.base?.[0]?.id || '',
        });
      } else {
        setSupportingNotes({
          top: suggestions.top?.[0]?.id || '',
          heart: suggestions.heart?.[0]?.id || '',
          base: suggestions.base?.[0]?.id || '',
        });
      }
    } catch (e) { /* silent */ }
    setSuggestLoading(false);
  }, [mainNotes, vibe, config]);

  const generateDescription = async () => {
    setDescLoading(true);
    try {
      const mainNames = mainNotes.map(id => config?.notes?.find(n => n.id === id)?.name || id);
      const supNames = Object.values(supportingNotes).filter(Boolean).map(id => config?.notes?.find(n => n.id === id)?.name || id);
      const res = await axios.post(`${API}/custom-scent/generate-description`, {
        name: scentName || 'Custom Blend',
        vibe,
        main_notes: mainNames,
        supporting_notes: supNames,
      });
      setAiDescription(res.data.description);
    } catch { toast.error('Could not generate description'); }
    setDescLoading(false);
  };

  const applyPreset = (preset) => {
    setVibe(preset.vibe);
    setMainNotes([preset.main_note]);
    setSupportingNotes({
      top: preset.supporting_notes?.[0] || '',
      heart: preset.supporting_notes?.[1] || '',
      base: preset.supporting_notes?.[2] || '',
    });
    setStep(2);
    toast.success(`"${preset.name}" template loaded`);
  };

  const handleSubmit = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast.error('Name and phone are required'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/custom-scent/orders`, {
        scent_name: scentName,
        vibe,
        main_notes: mainNotes,
        supporting_notes: Object.values(supportingNotes).filter(Boolean),
        size: selectedSize,
        customer_name: customerName,
        customer_email: customerEmail,
        phone: customerPhone,
        notes_to_maker: notesToMaker,
        ai_description: aiDescription,
      });
      setOrderPlaced(res.data);
      trackCustomScentSubmit();
      toast.success('Custom scent order placed!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    }
    setSubmitting(false);
  };

  const getNoteName = (id) => config?.notes?.find(n => n.id === id)?.name || id;
  const getNotesByCategory = (cat) => config?.notes?.filter(n => n.category === cat) || [];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
    </div>
  );

  if (!config?.enabled) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4">
      <div className="text-center">
        <p className="text-white/40 text-sm">Custom Scent Builder is currently unavailable.</p>
        <button onClick={() => navigate('/shop')} className="mt-4 text-[#D4AF37] text-sm underline">Browse Shop</button>
      </div>
    </div>
  );

  // Order placed success screen
  if (orderPlaced) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="custom-scent-success">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-16 h-16 mx-auto bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
          <Check className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h1 className="text-2xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Your Scent is Being Crafted
        </h1>
        <p className="text-white/50 text-sm">
          Order <span className="text-[#D4AF37] font-mono">{orderPlaced.order_number}</span> has been received.
          We'll start creating <span className="text-white">{orderPlaced.scent_name}</span> for you.
        </p>
        {aiDescription && (
          <div className="bg-[#141414] border border-white/10 p-4 text-left">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Your Scent</p>
            <p className="text-white/60 text-sm italic leading-relaxed">"{aiDescription}"</p>
          </div>
        )}
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate('/')} className="border border-white/20 text-white/60 px-6 py-2.5 text-sm hover:border-white/40 transition-colors">
            Home
          </button>
          <button onClick={() => navigate('/shop')} className="bg-[#D4AF37] text-black px-6 py-2.5 text-sm font-medium hover:bg-[#F3E5AB] transition-colors">
            Browse Shop
          </button>
        </div>
      </div>
    </div>
  );

  const totalSteps = 6;
  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="custom-scent-page">
      <SEOHead
        title="Custom Scent Builder"
        description="Design your own personalized EDP (30% oil, Extrait-level) with the JS Fragrance Custom Scent Builder. Choose your vibe, select your notes, and create a one-of-a-kind fragrance."
        url="https://jsfragrances.com/custom-scent"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Custom Scent', path: '/custom-scent' }]} />
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)} className="text-white/50 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Create Your Scent</p>
            <p className="text-white/30 text-[10px] mt-0.5">Step {step + 1} of {totalSteps}</p>
          </div>
          <div className="w-5" />
        </div>
        {/* Progress bar */}
        <div className="h-[2px] bg-white/5">
          <div className="h-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">

        {/* STEP 0: Choose Vibe */}
        {step === 0 && (
          <div className="space-y-8" data-testid="step-vibe">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Anong vibe gusto mo?
              </h2>
              <p className="text-white/40 text-sm mt-2">This determines the foundation of your scent.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {VIBE_OPTIONS.map(v => {
                const Icon = v.icon;
                const selected = vibe === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setVibe(v.id)}
                    data-testid={`vibe-${v.id.split(' ')[0].toLowerCase()}`}
                    className={`p-5 sm:p-6 border text-left transition-all duration-200 group ${
                      selected ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/10 hover:border-white/30 bg-[#141414]'
                    }`}
                  >
                    <Icon className="w-6 h-6 mb-3" style={{ color: selected ? v.color : 'rgba(255,255,255,0.3)' }} />
                    <p className={`text-sm font-medium ${selected ? 'text-white' : 'text-white/70'}`}>{v.label}</p>
                    <p className="text-white/30 text-xs mt-1">{v.desc}</p>
                    {selected && <Check className="w-4 h-4 text-[#D4AF37] mt-2" />}
                  </button>
                );
              })}
            </div>

            {/* Presets */}
            {config?.presets?.length > 0 && (
              <div className="pt-6 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-4">Or start from a template</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {config.presets.map(preset => {
                    const Icon = PRESET_ICONS[preset.icon] || Sparkles;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset)}
                        data-testid={`preset-${preset.id}`}
                        className="border border-white/10 bg-[#141414] hover:border-[#D4AF37]/40 p-4 text-left transition-all flex items-start gap-3 group"
                      >
                        <Icon className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-colors flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white text-sm font-medium">{preset.name}</p>
                          <p className="text-white/30 text-xs mt-0.5">{preset.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              onClick={() => { if (!vibe) { toast.error('Pumili ka muna ng vibe'); return; } setStep(1); }}
              disabled={!vibe}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="next-step-btn"
            >
              Next <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* STEP 1: Main Note */}
        {step === 1 && (
          <div className="space-y-8" data-testid="step-main-note">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Ano ang gusto mong maamoy?
              </h2>
              <p className="text-white/40 text-sm mt-2">Pick 1–2 dominant notes. Ito ang main character ng scent mo.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {filteredMainNotes.map(note => {
                const selected = mainNotes.includes(note.id);
                return (
                  <button
                    key={note.id}
                    onClick={() => toggleMainNote(note.id)}
                    data-testid={`note-${note.id}`}
                    className={`p-3 sm:p-4 border text-left transition-all ${
                      selected ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/10 hover:border-white/20 bg-[#141414]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white">{note.name}</span>
                      {selected && <Check className="w-3.5 h-3.5 text-[#D4AF37]" />}
                    </div>
                    <p className="text-white/30 text-[11px]">{note.description}</p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: FAMILY_COLORS[note.family] || '#666' }} />
                      <span className="text-[10px] text-white/20 uppercase tracking-wider">{note.category} · {note.family}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {mainNotes.length > 0 && (
              <div className="bg-[#141414] border border-white/10 p-3 flex items-center gap-2">
                <p className="text-white/40 text-xs">Selected:</p>
                {mainNotes.map(id => (
                  <span key={id} className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs px-2 py-1 border border-[#D4AF37]/20">
                    {getNoteName(id)}
                  </span>
                ))}
              </div>
            )}

            <button
              onClick={() => { if (!mainNotes.length) { toast.error('Pumili ka ng at least 1 main note'); return; } fetchSuggestions(); setStep(2); }}
              disabled={!mainNotes.length}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-30"
              data-testid="next-step-btn"
            >
              Next <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* STEP 2: Supporting Notes */}
        {step === 2 && (
          <div className="space-y-8" data-testid="step-supporting">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Supporting Notes
              </h2>
              <p className="text-white/40 text-sm mt-2">
                {suggestLoading ? 'AI is building your blend...' : 'We suggested the best combo. Adjust if you want.'}
              </p>
            </div>

            {suggestLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
              </div>
            ) : (
              <>
                {aiSuggestion?.explanation && (
                  <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">AI Recommendation</p>
                    </div>
                    <p className="text-white/60 text-sm">{aiSuggestion.explanation}</p>
                  </div>
                )}

                {/* Top note selector */}
                {['top', 'heart', 'base'].map(cat => (
                  <div key={cat}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-2 font-sans">
                      {cat === 'top' ? 'Top Note (first impression)' : cat === 'heart' ? 'Heart Note (main body)' : 'Base Note (lasting finish)'}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {getNotesByCategory(cat).filter(n => !mainNotes.includes(n.id)).map(note => {
                        const selected = supportingNotes[cat] === note.id;
                        return (
                          <button
                            key={note.id}
                            onClick={() => setSupportingNotes(prev => ({ ...prev, [cat]: note.id }))}
                            data-testid={`support-${cat}-${note.id}`}
                            className={`p-3 border text-left transition-all text-sm ${
                              selected ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-white' : 'border-white/10 text-white/50 hover:border-white/20 bg-[#141414]'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{note.name}</span>
                              {selected && <Check className="w-3 h-3 text-[#D4AF37]" />}
                            </div>
                            <p className="text-white/20 text-[10px] mt-0.5">{note.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Blend summary */}
                <div className="bg-[#141414] border border-white/10 p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Your Blend Formula</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/40">Main:</span>
                      <span className="text-white">{mainNotes.map(id => getNoteName(id)).join(' + ')}</span>
                    </div>
                    {supportingNotes.top && <div className="flex justify-between"><span className="text-white/40">Top:</span><span className="text-white/70">{getNoteName(supportingNotes.top)}</span></div>}
                    {supportingNotes.heart && <div className="flex justify-between"><span className="text-white/40">Heart:</span><span className="text-white/70">{getNoteName(supportingNotes.heart)}</span></div>}
                    {supportingNotes.base && <div className="flex justify-between"><span className="text-white/40">Base:</span><span className="text-white/70">{getNoteName(supportingNotes.base)}</span></div>}
                  </div>
                </div>
              </>
            )}

            <button
              onClick={() => setStep(3)}
              disabled={suggestLoading}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-30"
              data-testid="next-step-btn"
            >
              Next <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* STEP 3: Size & Price */}
        {step === 3 && (
          <div className="space-y-8" data-testid="step-size">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Choose Your Size
              </h2>
              <p className="text-white/40 text-sm mt-2">Select the bottle size.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {config?.sizes?.map((size, i) => {
                const selected = selectedSize?.size === size.size && selectedSize?.type === size.type;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedSize(size)}
                    data-testid={`size-${size.size}-${size.type}`}
                    className={`p-5 border text-left transition-all ${
                      selected ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/10 hover:border-white/20 bg-[#141414]'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{size.size}</p>
                        <p className="text-white/40 text-xs mt-1">{size.type}</p>
                      </div>
                      <p className="text-[#D4AF37] text-lg font-sans">₱{size.price?.toLocaleString()}</p>
                    </div>
                    {selected && <Check className="w-4 h-4 text-[#D4AF37] mt-3" />}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setStep(4)}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors"
              data-testid="next-step-btn"
            >
              Next <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* STEP 4: Name Your Scent */}
        {step === 4 && (
          <div className="space-y-8" data-testid="step-name">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Name Your Creation
              </h2>
              <p className="text-white/40 text-sm mt-2">Give it a name. Make it yours.</p>
            </div>

            <div className="max-w-sm mx-auto">
              <input
                type="text"
                value={scentName}
                onChange={e => { if (e.target.value.length <= (config?.max_name_length || 25)) setScentName(e.target.value); }}
                placeholder="e.g. MIDNIGHT EMBER"
                className="w-full bg-transparent border-b-2 border-white/20 text-white text-center text-xl py-4 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/15 uppercase tracking-[0.1em]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
                data-testid="scent-name-input"
              />
              <p className="text-white/20 text-xs text-center mt-2">
                {scentName.length}/{config?.max_name_length || 25} characters
              </p>
            </div>

            {/* AI Description */}
            <div className="space-y-3">
              <button
                onClick={generateDescription}
                disabled={descLoading || !scentName.trim()}
                className="mx-auto flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-5 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-30"
                data-testid="generate-description-btn"
              >
                {descLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate AI Description
              </button>
              {aiDescription && (
                <div className="bg-[#141414] border border-white/10 p-4 text-center">
                  <p className="text-white/60 text-sm italic leading-relaxed">"{aiDescription}"</p>
                </div>
              )}
            </div>

            <button
              onClick={() => { if (!scentName.trim()) { toast.error('Name your scent muna'); return; } setStep(5); }}
              disabled={!scentName.trim()}
              className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-30"
              data-testid="next-step-btn"
            >
              Review Order <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* STEP 5: Review & Submit */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-review">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Review Your Custom Scent
              </h2>
            </div>

            {/* Summary Card */}
            <div className="bg-[#141414] border border-white/10 p-5 space-y-4">
              <div className="text-center pb-4 border-b border-white/5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Your Creation</p>
                <h3 className="text-xl text-white mt-1 uppercase tracking-[0.1em]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{scentName}</h3>
                {aiDescription && <p className="text-white/40 text-xs italic mt-2">"{aiDescription}"</p>}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-white/30 text-xs block">Vibe</span><span className="text-white">{vibe}</span></div>
                <div><span className="text-white/30 text-xs block">Size</span><span className="text-white">{selectedSize?.size} {selectedSize?.type}</span></div>
                <div><span className="text-white/30 text-xs block">Main Notes</span><span className="text-white">{mainNotes.map(id => getNoteName(id)).join(', ')}</span></div>
                <div>
                  <span className="text-white/30 text-xs block">Supporting</span>
                  <span className="text-white/70 text-xs">
                    {Object.entries(supportingNotes).filter(([,v]) => v).map(([k,v]) => `${getNoteName(v)}`).join(', ')}
                  </span>
                </div>
              </div>

              <div className="border-t border-white/5 pt-3 flex justify-between items-center">
                <span className="text-white/40 text-sm">Total</span>
                <span className="text-[#D4AF37] text-xl font-sans">₱{selectedSize?.price?.toLocaleString()}</span>
              </div>
            </div>

            {/* Customer Details */}
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/30">Your Details</p>
              <input
                type="text" placeholder="Full Name *" value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
                data-testid="customer-name-input"
              />
              <input
                type="tel" placeholder="Phone Number *" value={customerPhone}
                onChange={e => setCustomerPhone(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
                data-testid="customer-phone-input"
              />
              <input
                type="email" placeholder="Email (optional)" value={customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
                data-testid="customer-email-input"
              />
              <textarea
                placeholder="Notes to maker (optional)" value={notesToMaker}
                onChange={e => setNotesToMaker(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20 resize-none h-20"
                data-testid="notes-to-maker-input"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !customerName.trim() || !customerPhone.trim()}
              className="w-full bg-[#D4AF37] text-black py-3.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="submit-order-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Placing Order...' : 'Place Custom Scent Order'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
